import { useEffect, useState } from 'react';
import { api, describeError, type BriefingSettings, type GoogleStatus } from './api.js';

/** Connect Gmail + Google Calendar, and set up the morning briefing. */
export function GoogleSettings() {
  const [google, setGoogle] = useState<GoogleStatus | null>(null);
  const [briefing, setBriefing] = useState<BriefingSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = () =>
    Promise.all([
      api.googleStatus().then(setGoogle),
      api.briefingSettings().then(setBriefing),
    ]).catch(() => {});

  useEffect(() => {
    void refresh();
    // Connecting happens in another tab: check again when Anthony comes back.
    const onShow = () => document.visibilityState === 'visible' && void refresh();
    document.addEventListener('visibilitychange', onShow);
    return () => document.removeEventListener('visibilitychange', onShow);
  }, []);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      setMessage(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const connect = () => {
    // Open the tab now, while it still counts as a tap (browsers block pop-ups opened later).
    const tab = window.open('', '_blank');
    void run(async () => {
      try {
        const { url } = await api.googleConnect();
        if (tab) tab.location.href = url;
        else window.location.href = url;
        setMessage('Finish on Google’s page, then come back here.');
      } catch (err) {
        tab?.close();
        throw err;
      }
    });
  };

  const saveBriefing = (next: BriefingSettings) =>
    void run(async () => {
      setBriefing(await api.setBriefing(next));
    });

  return (
    <>
      <section>
        <h3>Google</h3>
        <p className="muted small">
          Gmail and Google Calendar. Alfred reads and searches freely; emails, invites, changes and
          deletions wait for your approval.
        </p>
        {google && !google.configured && (
          <p className="small">Google isn’t set up on the server yet.</p>
        )}
        {google?.configured && google.connectedAs && (
          <>
            <p className="small">Connected as {google.connectedAs}.</p>
            <button
              className="btn btn-small btn-danger-outline"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await api.googleDisconnect();
                  setMessage('Disconnected. Alfred can no longer see your email or calendar.');
                })
              }
            >
              Disconnect
            </button>
          </>
        )}
        {google?.configured && !google.connectedAs && (
          <button className="btn btn-wide btn-primary" disabled={busy} onClick={connect}>
            Connect Google
          </button>
        )}
      </section>

      {briefing && (
        <section>
          <h3>Morning briefing</h3>
          <p className="muted small">
            A short rundown each morning: weather, today’s calendar, emails that need you, and
            anything happening near you.
          </p>
          <div className="row-gap">
            <label className="small">
              <input
                type="checkbox"
                checked={briefing.enabled}
                disabled={busy}
                onChange={(e) => saveBriefing({ ...briefing, enabled: e.target.checked })}
              />{' '}
              Every day at
            </label>
            <input
              type="time"
              className="time-input"
              value={briefing.time}
              disabled={busy || !briefing.enabled}
              aria-label="Briefing time"
              onChange={(e) =>
                e.target.value && saveBriefing({ ...briefing, time: e.target.value })
              }
            />
          </div>
          <button
            className="btn btn-small"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                setMessage('Writing it… (about 20 seconds)');
                await api.briefingNow();
                setMessage('Sent. It’s in the chat and on your phone.');
              })
            }
          >
            Send one now
          </button>
        </section>
      )}

      {message && <p className="small">{message}</p>}
    </>
  );
}
